import type { PropsWithChildren } from "react";
import { useState } from "react";
import { styled } from "styled-components";

import { ExpandableContent, MarkdownArea } from "common/components";
import { useTranslate } from "common/utilities/useTranslate";
import { KnowledgeButtons } from "domains/publication/components/KnowledgeButtons";
import { KnowledgeSignalSection } from "domains/signals/components/KnowledgeSignalSection";
import type { Signal } from "domains/signals/types";
import { getKnowledgeType } from "entities/knowledge/reducers";
import type { Source } from "entities/knowledge/types";

const StyledListLink = styled.button<{ $isExpandable?: boolean }>`
  background: none;
  border: none;
  font: inherit;
  text-align: left;
  color: inherit;
  display: flex;
  flex-direction: column;
  cursor: ${({ $isExpandable = true }) => ($isExpandable ? "pointer" : "default")};
`;

const StyledFooterRow = styled.div`
  margin-top: 10px;
`;

const StyledDescriptionContainer = styled.div`
  padding-top: 10px;
  width: 100%;
  border-top: ${({ theme }) => theme.borders.normal};
`;

const StyledDescription = styled.div`
  margin: 0;
  line-height: 1.5rem;
`;

const StyledTextContent = styled.div`
  padding-top: 10px;
`;

type CustomKnowledgeProps = {
  variant: "knowledge";
  link?: string;
  source: Source;
  isBookmarked: boolean;
  isPinned: boolean;
};

type KnowledgeProps = {
  variant: "customKnowledge";
  link?: never;
  source?: never;
  isBookmarked?: never;
  isPinned?: never;
};

type ExpandableKnowledgeItemWrapperProps = PropsWithChildren & {
  id: string;
  title: string;
  summary?: string;
  signals: Signal[];
  isExpandable?: boolean;
  shareLink: string;
} & (KnowledgeProps | CustomKnowledgeProps);

export const ExpandableKnowledgeItemWrapper = ({
  children,
  id,
  title,
  summary,
  source,
  isBookmarked,
  isPinned,
  signals,
  link,
  shareLink,
  isExpandable = true,
  variant,
}: ExpandableKnowledgeItemWrapperProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const translate = useTranslate();

  const toggleExpansion = () => {
    if (!isExpandable) return;
    setIsExpanded(!isExpanded);
  };

  const expandItemContentAriaText = translate("app.discover.topic.tabs.knowledge.expand_content");
  const collapseItemContentAriaText = translate("app.discover.topic.tabs.knowledge.collapse_content");

  return (
    <StyledListLink
      $isExpandable={isExpandable}
      onClick={toggleExpansion}
      aria-label={title}
      aria-description={isExpanded ? collapseItemContentAriaText : expandItemContentAriaText}
    >
      {children}
      <ExpandableContent isActive={isExpanded}>
        <StyledTextContent>
          <StyledDescriptionContainer>
            <StyledDescription>
              <MarkdownArea>{summary ?? translate("app.discover.topic.summary.no_description")}</MarkdownArea>
            </StyledDescription>
          </StyledDescriptionContainer>
          {link && variant === "knowledge" && (
            <StyledFooterRow>
              <KnowledgeButtons
                id={id}
                title={title}
                knowledgeType={getKnowledgeType(source)}
                isBookmarked={isBookmarked}
                isPinned={isPinned}
                link={link}
                shareLink={shareLink}
                fullwidth
                variant="knowledge"
              />
            </StyledFooterRow>
          )}
        </StyledTextContent>
        <KnowledgeSignalSection signals={signals} />
      </ExpandableContent>
    </StyledListLink>
  );
};
